/**
 * Supplier API Settings Page
 *
 * Configure API connections for dropshipping suppliers:
 * - AliExpress API
 * - Alibaba API
 * - Custom API
 * - Email orders
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Settings,
  Globe,
  Mail,
  Key,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

interface ApiConfig {
  type: 'aliexpress' | 'alibaba' | 'custom' | 'email' | 'none';
  auto_order_enabled?: boolean;
  order_prefix?: string;
  base_url?: string;
  products_endpoint?: string;
  order_endpoint?: string;
  auth_type?: 'none' | 'api_key' | 'basic' | 'bearer';
  headers?: Record<string, string>;
  field_mapping?: {
    product_id_field?: string;
    product_name_field?: string;
    product_price_field?: string;
    order_id_field?: string;
    items_field?: string;
  };
  price_multiplier?: number;
  pagination_type?: 'page' | 'offset' | 'cursor' | 'none';
  response_data_path?: string;
  order_email?: string;
  email_template?: string;
}

interface ApiCredentials {
  app_key?: string;
  app_secret?: string;
  access_token?: string;
  api_key?: string;
}

interface ApiSettingsResponse {
  supplier_id: string;
  supplier_name: string;
  supplier_type: string;
  api_config: ApiConfig;
  credentials: {
    has_credentials: boolean;
    fields_configured: string[];
  };
}

interface TestResult {
  success: boolean;
  message: string;
}

export default function SupplierApiSettingsPage() {
  const { t } = useTranslation('store');
  const { storeId, supplierId } = useParams<{ storeId: string; supplierId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [error, setError] = useState('');
  const [supplierName, setSupplierName] = useState('');

  const [apiConfig, setApiConfig] = useState<ApiConfig>({ type: 'none' });
  const [credentials, setCredentials] = useState<ApiCredentials>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [hasCredentials, setHasCredentials] = useState(false);
  const [configuredFields, setConfiguredFields] = useState<string[]>([]);

  useEffect(() => {
    fetchSettings();
  }, [storeId, supplierId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/api-settings`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json() as ApiSettingsResponse;
      setSupplierName(data.supplier_name);
      setApiConfig(data.api_config || { type: 'none' });
      setHasCredentials(data.credentials?.has_credentials || false);
      setConfiguredFields(data.credentials?.fields_configured || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await fetch(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/api-settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            api_config: apiConfig,
            credentials: Object.keys(credentials).length > 0 ? credentials : undefined,
            auto_order_enabled: apiConfig.auto_order_enabled,
            order_prefix: apiConfig.order_prefix,
          }),
        }
      );

      if (!response.ok) throw new Error('Failed to save settings');

      setHasCredentials(Object.keys(credentials).length > 0 || hasCredentials);
      setTestResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      const response = await fetch(
        `/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/api-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            api_config: apiConfig,
            credentials: Object.keys(credentials).length > 0 ? credentials : undefined,
            use_saved: Object.keys(credentials).length === 0 && hasCredentials,
          }),
        }
      );

      const result = await response.json() as TestResult;
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const toggleShowSecret = (field: string) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const renderCredentialField = (
    field: keyof ApiCredentials,
    label: string,
    placeholder: string
  ) => {
    const isConfigured = configuredFields.includes(field);
    const value = credentials[field] || '';
    const isSecret = field !== 'app_key';

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {isConfigured && !value && (
            <span className="ml-2 text-xs text-green-600 dark:text-green-400">
              ({t('suppliers.api.configured', 'Configured')})
            </span>
          )}
        </label>
        <div className="relative">
          <input
            type={isSecret && !showSecrets[field] ? 'password' : 'text'}
            value={value}
            onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
            placeholder={isConfigured && !value ? '••••••••••••' : placeholder}
            className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          {isSecret && (
            <button
              type="button"
              onClick={() => toggleShowSecret(field)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showSecrets[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/store/${storeId}/suppliers/${supplierId}`)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('suppliers.api.title', 'API Settings')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">{supplierName}</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* API Type Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('suppliers.api.type', 'Integration Type')}
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: 'aliexpress', label: 'AliExpress', icon: Globe, color: 'orange' },
            { value: 'alibaba', label: 'Alibaba', icon: Globe, color: 'amber' },
            { value: 'custom', label: t('suppliers.api.custom', 'Custom API'), icon: Settings, color: 'blue' },
            { value: 'email', label: t('suppliers.api.email', 'Email'), icon: Mail, color: 'green' },
          ].map(({ value, label, icon: Icon, color }) => (
            <button
              key={value}
              onClick={() => setApiConfig({ ...apiConfig, type: value as ApiConfig['type'] })}
              className={`p-4 rounded-lg border-2 transition-all ${
                apiConfig.type === value
                  ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-900/20`
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-8 h-8 mx-auto mb-2 ${
                apiConfig.type === value ? `text-${color}-600` : 'text-gray-400'
              }`} />
              <span className={`block text-sm font-medium ${
                apiConfig.type === value ? `text-${color}-700 dark:text-${color}-400` : 'text-gray-600 dark:text-gray-400'
              }`}>
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* API Credentials */}
      {(apiConfig.type === 'aliexpress' || apiConfig.type === 'alibaba') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Key className="w-5 h-5" />
            {t('suppliers.api.credentials', 'API Credentials')}
          </h2>

          <div className="space-y-4">
            {renderCredentialField('app_key', 'App Key', 'Enter your App Key')}
            {renderCredentialField('app_secret', 'App Secret', 'Enter your App Secret')}
            {renderCredentialField('access_token', 'Access Token', 'Enter your Access Token (optional)')}
          </div>

          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
              {t('suppliers.api.howToGet', 'How to get API credentials')}:
            </h3>
            <ol className="text-sm text-blue-700 dark:text-blue-300 list-decimal list-inside space-y-1">
              {apiConfig.type === 'aliexpress' ? (
                <>
                  <li>{t('suppliers.api.aliexpress.step1', 'Register for AliExpress Affiliate or DS Center')}</li>
                  <li>{t('suppliers.api.aliexpress.step2', 'Create an App in the Developer Console')}</li>
                  <li>{t('suppliers.api.aliexpress.step3', 'Copy your App Key and Secret')}</li>
                </>
              ) : (
                <>
                  <li>{t('suppliers.api.alibaba.step1', 'Register for Alibaba Open Platform')}</li>
                  <li>{t('suppliers.api.alibaba.step2', 'Create an Application')}</li>
                  <li>{t('suppliers.api.alibaba.step3', 'Copy your App Key and Secret')}</li>
                </>
              )}
            </ol>
          </div>
        </div>
      )}

      {/* Custom API Settings */}
      {apiConfig.type === 'custom' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('suppliers.api.customSettings', 'Custom API Settings')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suppliers.api.baseUrl', 'Base URL')}
              </label>
              <input
                type="url"
                value={apiConfig.base_url || ''}
                onChange={(e) => setApiConfig({ ...apiConfig, base_url: e.target.value })}
                placeholder="https://api.supplier.com"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('suppliers.api.productsEndpoint', 'Products Endpoint')}
                </label>
                <input
                  type="text"
                  value={apiConfig.products_endpoint || ''}
                  onChange={(e) => setApiConfig({ ...apiConfig, products_endpoint: e.target.value })}
                  placeholder="/products"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('suppliers.api.orderEndpoint', 'Order Endpoint')}
                </label>
                <input
                  type="text"
                  value={apiConfig.order_endpoint || ''}
                  onChange={(e) => setApiConfig({ ...apiConfig, order_endpoint: e.target.value })}
                  placeholder="/orders"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suppliers.api.authType', 'Authentication Type')}
              </label>
              <select
                value={apiConfig.auth_type || 'none'}
                onChange={(e) => setApiConfig({ ...apiConfig, auth_type: e.target.value as ApiConfig['auth_type'] })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="none">{t('suppliers.api.authNone', 'None')}</option>
                <option value="api_key">{t('suppliers.api.authApiKey', 'API Key (Header)')}</option>
                <option value="bearer">{t('suppliers.api.authBearer', 'Bearer Token')}</option>
                <option value="basic">{t('suppliers.api.authBasic', 'Basic Auth')}</option>
              </select>
            </div>

            {apiConfig.auth_type && apiConfig.auth_type !== 'none' && (
              <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                {apiConfig.auth_type === 'basic' ? (
                  <>
                    {renderCredentialField('app_key', t('suppliers.api.username', 'Username'), 'Username')}
                    {renderCredentialField('app_secret', t('suppliers.api.password', 'Password'), 'Password')}
                  </>
                ) : (
                  renderCredentialField('api_key', t('suppliers.api.apiKey', 'API Key'), 'Enter API Key')
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suppliers.api.responseDataPath', 'Response Data Path')}
              </label>
              <input
                type="text"
                value={apiConfig.response_data_path || ''}
                onChange={(e) => setApiConfig({ ...apiConfig, response_data_path: e.target.value })}
                placeholder="data.products"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('suppliers.api.responseDataPathHelp', 'Path to array of products in API response (e.g., data.products)')}
              </p>
            </div>

            {/* Field Mappings */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t('suppliers.api.fieldMappings', 'Field Mappings')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Product ID Field</label>
                  <input
                    type="text"
                    value={apiConfig.field_mapping?.product_id_field || ''}
                    onChange={(e) => setApiConfig({
                      ...apiConfig,
                      field_mapping: { ...apiConfig.field_mapping, product_id_field: e.target.value }
                    })}
                    placeholder="id"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Product Name Field</label>
                  <input
                    type="text"
                    value={apiConfig.field_mapping?.product_name_field || ''}
                    onChange={(e) => setApiConfig({
                      ...apiConfig,
                      field_mapping: { ...apiConfig.field_mapping, product_name_field: e.target.value }
                    })}
                    placeholder="name"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price Field</label>
                  <input
                    type="text"
                    value={apiConfig.field_mapping?.product_price_field || ''}
                    onChange={(e) => setApiConfig({
                      ...apiConfig,
                      field_mapping: { ...apiConfig.field_mapping, product_price_field: e.target.value }
                    })}
                    placeholder="price"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price Multiplier</label>
                  <input
                    type="number"
                    step="0.01"
                    value={apiConfig.price_multiplier || 1}
                    onChange={(e) => setApiConfig({ ...apiConfig, price_multiplier: parseFloat(e.target.value) || 1 })}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Settings */}
      {apiConfig.type === 'email' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('suppliers.api.emailSettings', 'Email Order Settings')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suppliers.api.orderEmail', 'Order Email Address')}
              </label>
              <input
                type="email"
                value={apiConfig.order_email || ''}
                onChange={(e) => setApiConfig({ ...apiConfig, order_email: e.target.value })}
                placeholder="orders@supplier.com"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('suppliers.api.emailTemplate', 'Email Template (optional)')}
              </label>
              <textarea
                value={apiConfig.email_template || ''}
                onChange={(e) => setApiConfig({ ...apiConfig, email_template: e.target.value })}
                placeholder="Order #{order_id}&#10;&#10;Items:&#10;{items}&#10;&#10;Ship to:&#10;{shipping_address}"
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('suppliers.api.emailTemplateHelp', 'Use {order_id}, {items}, {shipping_address} placeholders')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* General Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('suppliers.api.generalSettings', 'Order Settings')}
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('suppliers.api.autoOrder', 'Auto-submit orders')}
              </span>
              <p className="text-xs text-gray-500">
                {t('suppliers.api.autoOrderHelp', 'Automatically submit orders to supplier when customer pays')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={apiConfig.auto_order_enabled || false}
                onChange={(e) => setApiConfig({ ...apiConfig, auto_order_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('suppliers.api.orderPrefix', 'Order Reference Prefix')}
            </label>
            <input
              type="text"
              value={apiConfig.order_prefix || ''}
              onChange={(e) => setApiConfig({ ...apiConfig, order_prefix: e.target.value })}
              placeholder="SO"
              maxLength={10}
              className="w-32 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
            />
          </div>
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
        }`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span>{testResult.message}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between gap-4">
        <button
          onClick={handleTest}
          disabled={testing || apiConfig.type === 'none'}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
        >
          {testing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {t('suppliers.api.testConnection', 'Test Connection')}
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {t('common.save', 'Save Settings')}
        </button>
      </div>
    </div>
  );
}
